import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Result, Skeleton, Table, Tag, Tooltip, message } from "antd";
import { ArrowLeftOutlined, FilePdfOutlined, PrinterOutlined } from "@ant-design/icons";
import * as dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import { DetailLayout, KeyValueList, Num, PageHeader, SectionCard } from "components/shared-components/purchase";
import Barcode from "components/shared-components/Table/Barcode";
import OrderDrawer from "components/shared-components/Orders/OrderDrawer";
import { CURRENCY } from "constants/DeliveryConstants";
import { DATE_FORMAT_DD_MM_YYYY, DATE_FORMAT_DD_MM_YYYY_HH_mm } from "constants/DateConstant";
import { formatPrice } from "functions/formatters";
import { getBarcodeDataExact } from "functions/orders";
import { getExactSalesOrder, getExactOrderInvoices } from "functions/api/exact";
import {
  EXACT_DELIVERY_STATUS_META,
  EXACT_INVOICE_STATUS_META,
  EXACT_ORDER_STATUS_META,
  getLineOpenQuantity,
  getOrderLines,
  parseExactDate,
  summariseExactOrder,
} from "functions/exactOrders";
import { useScopedOrders } from "data/hooks/useScopedOrders";
import urls from "urls";

/**
 * Eén verkooporder uit Exact, als eigen pagina.
 *
 * Eigen route (en dus een eigen `orderId` in de URL) in plaats van een drawer
 * over de lijst: de link is dan deelbaar en overleeft een refresh. Dat kan
 * omdat de order zelfstandig op te halen is via `/api/exact/orders/{orderId}`;
 * hij komt níet uit de lijst in redux, want die leeft alleen in het geheugen en
 * is na een herlaadbeurt leeg.
 *
 * Bewust géén "openen in Exact Online"-knop: die URL heeft de administratiecode
 * (`divisionCode`) nodig en die staat alleen server-side in de API-configuratie.
 * Een half kloppende link is erger dan geen link — en het doel van deze pagina
 * is juist dat Exact Online niet meer nodig is.
 */
const ExactOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const barcodeRef = React.useRef();

  const products = useSelector((state) => state.firestoreReducer.ordered.products);
  const customers = useSelector((state) => state.firestoreReducer.ordered.webshopUsers);
  const webshopOrders = useScopedOrders();

  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [webshopOrderOpen, setWebshopOrderOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);

    getExactSalesOrder(orderId)
      .then((data) => {
        if (cancelled) return;
        setOrder(data);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Ophalen Exact-bestelling mislukt:", error);
        setLoadError(error?.response?.status === 404 ? "notfound" : "error");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const summary = useMemo(() => (order ? summariseExactOrder(order, customers) : null), [order, customers]);
  const lines = useMemo(() => getOrderLines(order), [order]);

  // De webshoporder waar deze Exact-order uit is ontstaan: de API zet ons eigen
  // `orderId` in Exact's `YourRef`. Alleen te vinden zolang de order binnen het
  // ingestelde venster van de orderlijst valt; daarbuiten tonen we de referentie
  // gewoon als tekst.
  const webshopOrder = useMemo(() => {
    if (!summary?.yourRef || !webshopOrders) return null;
    return webshopOrders.find((candidate) => candidate.orderId === summary.yourRef) || null;
  }, [summary, webshopOrders]);

  useEffect(() => {
    if (!summary?.yourRef) return;
    let cancelled = false;
    setInvoicesLoading(true);
    getExactOrderInvoices(summary.yourRef)
      .then((data) => {
        if (!cancelled) setInvoices(data);
      })
      .catch(() => {
        // Geen factuur gevonden is geen fout: een order zonder factuur is normaal.
        if (!cancelled) setInvoices([]);
      })
      .finally(() => {
        if (!cancelled) setInvoicesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [summary?.yourRef]);

  const handleBarcodesPrint = useReactToPrint({
    contentRef: barcodeRef,
    onAfterPrint: () => setIsPrinting(false),
  });

  const openInvoice = (base64, index) => {
    try {
      const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      // Pas opruimen nadat de nieuwe tab hem heeft geladen.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("Openen factuur-PDF mislukt:", error);
      messageApi.error(t("exact.invoice_open_failed", { number: index + 1 }));
    }
  };

  const formatDate = (ms) => (ms === null ? "-" : dayjs(ms).format(DATE_FORMAT_DD_MM_YYYY));

  const statusTag = (status, meta = EXACT_ORDER_STATUS_META, group = "status") => {
    const entry = meta[status];
    if (!entry) return <Tag>{t("exact.status.unknown")}</Tag>;
    return <Tag color={entry.color}>{t(`exact.${group}.${entry.key}`)}</Tag>;
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader title={t("order.exact")} crumbLabel={null} />
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (loadError || !order) {
    return (
      <Result
        status={loadError === "notfound" ? "404" : "error"}
        title={loadError === "notfound" ? t("exact.order_not_found") : t("notifications.error_retrieving_exact_orders")}
        extra={
          <Button type="primary" onClick={() => navigate(urls.exactOrdersList)}>
            {t("back")}
          </Button>
        }
      />
    );
  }

  const lineColumns = [
    {
      title: t("product_code"),
      dataIndex: "ItemCode",
      width: 130,
      render: (value) => <Num>{value || "-"}</Num>,
    },
    {
      title: t("product"),
      dataIndex: "ItemDescription",
      render: (value, line) => value || line.Description || "-",
    },
    {
      // Besteld, geleverd en open zaten in drie kolommen. Eén kolom met het
      // bestelde aantal en de rest op hover, net als op het overzicht.
      title: t("exact.ordered"),
      dataIndex: "Quantity",
      width: 100,
      align: "right",
      render: (value, line) => (
        <Tooltip
          title={`${line.QuantityDelivered ?? 0} / ${value ?? 0} ${t("exact.delivered").toLowerCase()}`}
        >
          <Num align="right" strong={getLineOpenQuantity(line) > 0}>
            {value ?? 0}
          </Num>
        </Tooltip>
      ),
    },
    {
      title: t("exact.unit_price"),
      dataIndex: "UnitPrice",
      width: 110,
      align: "right",
      render: (value) => <Num align="right">{CURRENCY + formatPrice(value)}</Num>,
    },
    {
      title: t("exact.line_amount"),
      dataIndex: "AmountDC",
      width: 120,
      align: "right",
      render: (value) => <Num align="right">{CURRENCY + formatPrice(value)}</Num>,
    },
  ];

  const details = [
    { label: t("exact.order_number"), value: <Num>{summary.orderNumber ?? "-"}</Num> },
    {
      label: t("exact.delivery"),
      value: statusTag(summary.deliveryStatus, EXACT_DELIVERY_STATUS_META, "delivery_status"),
    },
    {
      label: t("exact.invoicing"),
      value: statusTag(summary.invoiceStatus, EXACT_INVOICE_STATUS_META, "invoice_status"),
    },
    { label: t("exact.overall_status"), value: statusTag(summary.status) },
    { label: t("exact.reference"), value: <Num>{summary.yourRef || "-"}</Num> },
    { label: t("order_date"), value: formatDate(summary.orderDate) },
    { label: t("exact.delivery_date"), value: formatDate(summary.deliveryDate) },
    { label: t("customer.key"), value: summary.customerName || "-", wrap: true },
    summary.salesperson ? { label: t("exact.salesperson"), value: summary.salesperson } : null,
    order.WarehouseDescription ? { label: t("exact.warehouse"), value: order.WarehouseDescription } : null,
    order.ShippingMethodDescription
      ? { label: t("exact.shipping_method"), value: order.ShippingMethodDescription, wrap: true }
      : null,
    order.PaymentConditionDescription
      ? { label: t("exact.payment_condition"), value: order.PaymentConditionDescription, wrap: true }
      : null,
  ].filter(Boolean);

  const amounts = [
    { label: t("exact.order_value"), value: <Num strong>{CURRENCY + formatPrice(summary.amount)}</Num> },
    order.AmountDiscountExclVat
      ? { label: t("discount"), value: <Num>{CURRENCY + formatPrice(order.AmountDiscountExclVat)}</Num> }
      : null,
    { label: t("exact.currency"), value: summary.currency },
    { label: t("exact.ordered"), value: <Num>{summary.orderedQuantity}</Num> },
    { label: t("exact.delivered"), value: <Num>{summary.deliveredQuantity}</Num> },
    {
      label: t("exact.open_quantity"),
      value: (
        <Num strong={summary.openQuantity > 0} muted={summary.openQuantity === 0}>
          {summary.openQuantity}
        </Num>
      ),
    },
  ].filter(Boolean);

  const trail = [
    order.Created
      ? { label: t("exact.created"), value: dayjs(parseExactDate(order.Created)).format(DATE_FORMAT_DD_MM_YYYY_HH_mm) }
      : null,
    order.Modified
      ? {
          label: t("exact.modified"),
          value: dayjs(parseExactDate(order.Modified)).format(DATE_FORMAT_DD_MM_YYYY_HH_mm),
        }
      : null,
    { label: t("exact.order_id"), value: <Num muted>{summary.id}</Num>, wrap: true },
  ].filter(Boolean);

  return (
    <div>
      {contextHolder}
      <PageHeader
        title={summary.orderNumber ? `${t("exact.order")} ${summary.orderNumber}` : t("exact.order")}
        crumbLabel={summary.orderNumber ? String(summary.orderNumber) : summary.yourRef || "…"}
        subtitle={[summary.customerName, formatDate(summary.orderDate), summary.yourRef].filter(Boolean).join(" · ")}
        extra={
          <>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(urls.exactOrdersList)}>
              {t("back")}
            </Button>
            {webshopOrder ? (
              <Button onClick={() => setWebshopOrderOpen(true)}>{t("exact.open_webshop_order")}</Button>
            ) : null}
            <Button
              icon={<PrinterOutlined />}
              onClick={() => {
                setIsPrinting(true);
                setTimeout(() => handleBarcodesPrint(), 1000);
              }}
            >
              {t("order.barcodes")}
            </Button>
          </>
        }
      />

      {summary.isBackorder ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 20 }}
          message={t("exact.backorder_notice", { open: summary.openQuantity, ordered: summary.orderedQuantity })}
        />
      ) : null}

      <DetailLayout
        aside={
          <>
            <SectionCard title={t("exact.details")}>
              <KeyValueList items={details} />
            </SectionCard>
            <SectionCard title={t("exact.amounts")}>
              <KeyValueList items={amounts} />
            </SectionCard>
            <SectionCard title={t("exact.trail")}>
              <KeyValueList items={trail} labelWidth={118} />
            </SectionCard>
          </>
        }
      >
        <SectionCard title={t("order.products")} meta={t("exact.line_count_plural", { count: lines.length })}>
          <Table
            columns={lineColumns}
            dataSource={lines}
            rowKey={(line) => line.ID || `${line.LineNumber}-${line.ItemCode}`}
            pagination={false}
            size="small"
            scroll={{ x: 760 }}
          />
        </SectionCard>

        {order.Remarks || order.Description ? (
          <SectionCard title={t("notes")} padded>
            {order.Description ? <div style={{ marginBottom: order.Remarks ? 10 : 0 }}>{order.Description}</div> : null}
            {order.Remarks ? <div style={{ whiteSpace: "pre-wrap" }}>{order.Remarks}</div> : null}
          </SectionCard>
        ) : null}

        <SectionCard title={t("invoice")} meta={invoicesLoading ? t("loading") : null}>
          <div style={{ padding: "14px 20px" }}>
            {invoicesLoading ? (
              <Skeleton active paragraph={{ rows: 1 }} />
            ) : invoices.length ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {invoices.map((base64, index) => (
                  <Button key={index} icon={<FilePdfOutlined />} onClick={() => openInvoice(base64, index)}>
                    {t("exact.open_invoice", { number: index + 1 })}
                  </Button>
                ))}
              </div>
            ) : (
              <Tooltip title={summary.yourRef ? null : t("exact.invoice_needs_reference")}>
                <span style={{ color: "rgba(0, 0, 0, 0.45)", fontSize: 13 }}>{t("exact.no_invoice")}</span>
              </Tooltip>
            )}
          </div>
        </SectionCard>
      </DetailLayout>

      <OrderDrawer order={webshopOrder} open={webshopOrderOpen} closeDrawer={() => setWebshopOrderOpen(false)} />

      <div style={{ display: "none" }}>
        {isPrinting ? <Barcode ref={barcodeRef} data={getBarcodeDataExact([order], products)} /> : null}
      </div>
    </div>
  );
};

export default ExactOrderDetail;
